import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { HtmlEditor } from 'devextreme-react/html-editor';

// Helper to create unique storage keys based on question ID
const getStorageKey = (questionId) => `questionHighlights_${questionId}`;
const getStrikethroughStorageKey = (questionId) => `questionStrikethroughs_${questionId}`;

// Helper functions
const getPathTo = (node, root) => {
    if (node === root) return '';
    if (!root.contains(node)) return null;
    const path = [];
    let current = node;
    while (current !== root) {
        const parent = current.parentNode;
        if (!parent) return null;
        const index = Array.from(parent.childNodes).indexOf(current);
        path.unshift(index);
        current = parent;
    }
    return path.join('.');
};

// Helper function to compare DOM paths for positional order
// Returns: negative if path1 comes before path2, positive if after, 0 if equal
const comparePaths = (path1, offset1, path2, offset2) => {
    if (path1 === path2) {
        return offset1 - offset2;
    }

    const parts1 = path1.split('.').map(Number);
    const parts2 = path2.split('.').map(Number);

    // Compare path parts
    for (let i = 0; i < Math.min(parts1.length, parts2.length); i++) {
        if (parts1[i] !== parts2[i]) {
            return parts1[i] - parts2[i];
        }
    }

    // One path is a prefix of the other
    return parts1.length - parts2.length;
};

const getNodeFromPath = (path, root) => {
    if (!path) return null;
    if (path === '') return root;
    try {
        return path.split('.').reduce((current, index) => {
            if (!current || !current.childNodes) return null;
            return current.childNodes[parseInt(index, 10)];
        }, root);
    } catch (error) {
        console.warn('Could not find node from path:', path, error);
        return null;
    }
};

const QuestionTextViewer = forwardRef(({ questionId, htmlContent, style, className }, ref) => {
    const [selectedText, setSelectedText] = useState('');
    const [highlightBoxes, setHighlightBoxes] = useState([]);
    const [strikethroughBoxes, setStrikethroughBoxes] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [strikethroughs, setStrikethroughs] = useState([]);

    const containerRef = useRef(null);
    const hasLoadedHighlightsRef = useRef(false);
    const hasLoadedStrikethroughsRef = useRef(false);

    // Load highlights when questionId changes
    useEffect(() => {
        if (!questionId) {
            setHighlights([]);
            hasLoadedHighlightsRef.current = true;
            return;
        }

        const storageKey = getStorageKey(questionId);
        try {
            const saved = localStorage.getItem(storageKey);
            setHighlights(saved ? JSON.parse(saved) : []);
        } catch (error) {
            console.error("Failed to load highlights from localStorage:", error);
            setHighlights([]);
        }
        hasLoadedHighlightsRef.current = true;
    }, [questionId]);

    // Save highlights to localStorage
    useEffect(() => {
        if (!hasLoadedHighlightsRef.current || !questionId) return;
        const storageKey = getStorageKey(questionId);
        localStorage.setItem(storageKey, JSON.stringify(highlights));
    }, [highlights, questionId]);

    // Load strikethroughs when questionId changes
    useEffect(() => {
        if (!questionId) {
            setStrikethroughs([]);
            hasLoadedStrikethroughsRef.current = true;
            return;
        }

        const storageKey = getStrikethroughStorageKey(questionId);
        try {
            const saved = localStorage.getItem(storageKey);
            setStrikethroughs(saved ? JSON.parse(saved) : []);
        } catch (error) {
            console.error("Failed to load strikethroughs from localStorage:", error);
            setStrikethroughs([]);
        }
        hasLoadedStrikethroughsRef.current = true;
    }, [questionId]);

    // Save strikethroughs to localStorage
    useEffect(() => {
        if (!hasLoadedStrikethroughsRef.current || !questionId) return;
        const storageKey = getStrikethroughStorageKey(questionId);
        localStorage.setItem(storageKey, JSON.stringify(strikethroughs));
    }, [strikethroughs, questionId]);

    const updateHighlightBoxes = useCallback(() => {
        if (!containerRef.current) return;

        const contentElement = containerRef.current.querySelector('.dx-htmleditor-content');
        if (!contentElement) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const allBoxes = [];

        highlights.forEach(highlight => {
            const startNode = getNodeFromPath(highlight.startPath, contentElement);
            const endNode = getNodeFromPath(highlight.endPath, contentElement);

            if (!startNode || !endNode) return;

            try {
                const range = document.createRange();
                range.setStart(startNode, highlight.startOffset);
                range.setEnd(endNode, highlight.endOffset);

                const rects = Array.from(range.getClientRects());

                rects.forEach((rect, index) => {
                    allBoxes.push({
                        key: `${highlight.id}-${index}`,
                        top: rect.top - containerRect.top + containerRef.current.scrollTop,
                        left: rect.left - containerRect.left + containerRef.current.scrollLeft,
                        width: rect.width,
                        height: rect.height,
                        color: highlight.color || 'yellow',
                    });
                });
            } catch (error) {
                console.warn(`Could not render highlight ${highlight.id}:`, error);
            }
        });

        setHighlightBoxes(allBoxes);
    }, [highlights]);

    const updateStrikethroughBoxes = useCallback(() => {
        if (!containerRef.current) return;

        const contentElement = containerRef.current.querySelector('.dx-htmleditor-content');
        if (!contentElement) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const allBoxes = [];

        strikethroughs.forEach(strikethrough => {
            const startNode = getNodeFromPath(strikethrough.startPath, contentElement);
            const endNode = getNodeFromPath(strikethrough.endPath, contentElement);

            if (!startNode || !endNode) return;

            try {
                const range = document.createRange();
                range.setStart(startNode, strikethrough.startOffset);
                range.setEnd(endNode, strikethrough.endOffset);

                const rects = Array.from(range.getClientRects());

                rects.forEach((rect, index) => {
                    allBoxes.push({
                        key: `${strikethrough.id}-${index}`,
                        top: rect.top - containerRect.top + containerRef.current.scrollTop,
                        left: rect.left - containerRect.left + containerRef.current.scrollLeft,
                        width: rect.width,
                        height: rect.height,
                    });
                });
            } catch (error) {
                console.warn(`Could not render strikethrough ${strikethrough.id}:`, error);
            }
        });

        setStrikethroughBoxes(allBoxes);
    }, [strikethroughs]);

    useEffect(() => {
        const timer = setTimeout(() => {
            updateHighlightBoxes();
            updateStrikethroughBoxes();
        }, 100);
        return () => clearTimeout(timer);
    }, [highlights, strikethroughs, htmlContent, updateHighlightBoxes, updateStrikethroughBoxes]);

    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver(() => {
            setTimeout(() => {
                updateHighlightBoxes();
                updateStrikethroughBoxes();
            }, 50);
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [updateHighlightBoxes, updateStrikethroughBoxes]);

    const handleHighlightSelected = (color = 'yellow') => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) {
            console.log('No selection found for highlight');
            return;
        }

        const range = selection.getRangeAt(0);
        const text = selection.toString();
        if (!text.trim()) {
            console.log('No text selected for highlight');
            return;
        }

        console.log('Selected text for highlight:', text, 'with color:', color);

        const contentElement = containerRef.current?.querySelector('.dx-htmleditor-content');
        if (!contentElement) {
            console.warn('Could not find .dx-htmleditor-content element');
            // Try alternative selectors
            const altContentElement = containerRef.current?.querySelector('.dx-htmleditor');
            if (altContentElement) {
                console.log('Found alternative content element, trying highlight');
                // Check if there's already a highlight in this exact location
                const selectionStart = getPathTo(range.startContainer, altContentElement) + '.' + range.startOffset;
                const selectionEnd = getPathTo(range.endContainer, altContentElement) + '.' + range.endOffset;

                const existingHighlight = highlights.find(highlight => {
                    const highlightStart = highlight.startPath + '.' + highlight.startOffset;
                    const highlightEnd = highlight.endPath + '.' + highlight.endOffset;
                    return selectionStart === highlightStart && selectionEnd === highlightEnd;
                });

                if (existingHighlight) {
                    // Toggle: remove existing highlight
                    setHighlights(prev => prev.filter(h => h.id !== existingHighlight.id));
                } else {
                    // Add new highlight
                    const newHighlight = {
                        id: 'highlight-' + Date.now(),
                        text: text,
                        color: color,
                        startPath: getPathTo(range.startContainer, altContentElement),
                        startOffset: range.startOffset,
                        endPath: getPathTo(range.endContainer, altContentElement),
                        endOffset: range.endOffset,
                    };
                    setHighlights(prev => [...prev, newHighlight]);
                }
                return;
            }
            alert('Could not find the content element. Please try again.');
            return;
        }

        // Check if the selection is within our content
        if (!contentElement.contains(range.commonAncestorContainer)) {
            return;
        }

        // Check for overlapping highlights and implement smart highlighting behavior
        const selectionStartPath = getPathTo(range.startContainer, contentElement);
        const selectionEndPath = getPathTo(range.endContainer, contentElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        // Find overlapping highlights
        const overlappingHighlights = highlights.filter(highlight => {
            // Compare highlight.start with selection.end
            const highlightStartVsSelectionEnd = comparePaths(highlight.startPath, highlight.startOffset, selectionEndPath, selectionEndOffset);
            // Compare highlight.end with selection.start
            const highlightEndVsSelectionStart = comparePaths(highlight.endPath, highlight.endOffset, selectionStartPath, selectionStartOffset);

            // Overlap exists if: highlight starts before selection ends AND highlight ends after selection starts
            return highlightStartVsSelectionEnd < 0 && highlightEndVsSelectionStart > 0;
        });

        if (overlappingHighlights.length > 0) {
            // Check if ALL overlapping highlights have the same color as the new color
            const allSameColor = overlappingHighlights.every(h => h.color === color);

            if (allSameColor) {
                // All overlapping highlights are the same color - toggle (remove highlight from selected portion)
                setHighlights(prev => {
                    const newHighlights = [];

                    // Get all overlapping highlight IDs
                    const overlappingIds = overlappingHighlights.map(h => h.id);

                    prev.forEach(highlight => {
                        if (overlappingIds.includes(highlight.id)) {
                            // Split this highlight, removing the selected portion
                            const highlightStartComparison = comparePaths(highlight.startPath, highlight.startOffset, selectionStartPath, selectionStartOffset);
                            const highlightEndComparison = comparePaths(highlight.endPath, highlight.endOffset, selectionEndPath, selectionEndOffset);

                            if (highlightStartComparison < 0) {
                                // Keep part before selection
                                const beforeHighlight = {
                                    ...highlight,
                                    id: highlight.id + '_before_' + Date.now(),
                                    endPath: selectionStartPath,
                                    endOffset: selectionStartOffset
                                };
                                newHighlights.push(beforeHighlight);
                            }

                            if (highlightEndComparison > 0) {
                                // Keep part after selection
                                const afterHighlight = {
                                    ...highlight,
                                    id: highlight.id + '_after_' + Date.now(),
                                    startPath: selectionEndPath,
                                    startOffset: selectionEndOffset
                                };
                                newHighlights.push(afterHighlight);
                            }
                        } else {
                            // Keep other highlights unchanged
                            newHighlights.push(highlight);
                        }
                    });

                    return newHighlights;
                });
            } else {
                // Different color or mixed colors - remove ALL existing highlights from selection and apply new color
                setHighlights(prev => {
                    const newHighlights = [];

                    // Process all overlapping highlights (any color)
                    const overlappingIds = overlappingHighlights.map(h => h.id);

                    prev.forEach(highlight => {
                        if (overlappingIds.includes(highlight.id)) {
                            // This highlight overlaps - split and keep non-overlapping parts
                            const highlightStartComparison = comparePaths(highlight.startPath, highlight.startOffset, selectionStartPath, selectionStartOffset);
                            const highlightEndComparison = comparePaths(highlight.endPath, highlight.endOffset, selectionEndPath, selectionEndOffset);

                            if (highlightStartComparison < 0) {
                                const beforeHighlight = {
                                    ...highlight,
                                    id: highlight.id + '_before_' + Date.now(),
                                    endPath: selectionStartPath,
                                    endOffset: selectionStartOffset
                                };
                                newHighlights.push(beforeHighlight);
                            }

                            if (highlightEndComparison > 0) {
                                const afterHighlight = {
                                    ...highlight,
                                    id: highlight.id + '_after_' + Date.now(),
                                    startPath: selectionEndPath,
                                    startOffset: selectionEndOffset
                                };
                                newHighlights.push(afterHighlight);
                            }
                        } else {
                            // No overlap - keep as is
                            newHighlights.push(highlight);
                        }
                    });

                    // Add the new highlight
                    const newHighlight = {
                        id: 'highlight-' + Date.now(),
                        text: text,
                        color: color,
                        startPath: selectionStartPath,
                        startOffset: selectionStartOffset,
                        endPath: selectionEndPath,
                        endOffset: selectionEndOffset,
                    };
                    newHighlights.push(newHighlight);

                    return newHighlights;
                });
            }
        } else {
            // No overlapping highlights - add new highlight
            const newHighlight = {
                id: 'highlight-' + Date.now(),
                text: text,
                color: color,
                startPath: selectionStartPath,
                startOffset: selectionStartOffset,
                endPath: selectionEndPath,
                endOffset: selectionEndOffset,
            };
            setHighlights(prev => [...prev, newHighlight]);
        }

        // Keep selection active for potential additional operations
        // selection.removeAllRanges();
        // setSelectedText('');
    };

    const removeHighlightFromSelection = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const contentElement = containerRef.current?.querySelector('.dx-htmleditor-content');
        if (!contentElement || !contentElement.contains(range.commonAncestorContainer)) {
            return;
        }

        // Find highlights that are completely contained within the current selection
        const selectionStartPath = getPathTo(range.startContainer, contentElement);
        const selectionEndPath = getPathTo(range.endContainer, contentElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        setHighlights(prev => {
            const newHighlights = [];

            prev.forEach(highlight => {
                const highlightStartPath = highlight.startPath;
                const highlightEndPath = highlight.endPath;
                const highlightStartOffset = highlight.startOffset;
                const highlightEndOffset = highlight.endOffset;

                // Check if highlight overlaps with the selection
                // Compare highlight.start with selection.end
                const highlightStartVsSelectionEnd = comparePaths(highlightStartPath, highlightStartOffset, selectionEndPath, selectionEndOffset);
                // Compare highlight.end with selection.start
                const highlightEndVsSelectionStart = comparePaths(highlightEndPath, highlightEndOffset, selectionStartPath, selectionStartOffset);

                // Check if highlight overlaps with selection
                // Overlap exists if: highlight starts before selection ends AND highlight ends after selection starts
                const hasOverlap = highlightStartVsSelectionEnd < 0 && highlightEndVsSelectionStart > 0;

                if (!hasOverlap) {
                    // No overlap - keep the highlight as is
                    newHighlights.push(highlight);
                } else {
                    // Overlap detected - split the highlight
                    // Compare positions for splitting
                    const highlightStartComparison = comparePaths(highlightStartPath, highlightStartOffset, selectionStartPath, selectionStartOffset);
                    const highlightEndComparison = comparePaths(highlightEndPath, highlightEndOffset, selectionEndPath, selectionEndOffset);

                    // Create highlight segments that don't overlap with selection
                    if (highlightStartComparison < 0) {
                        // Highlight starts before selection - keep the part before selection
                        newHighlights.push({
                            ...highlight,
                            id: highlight.id + '_before_' + Date.now(),
                            endPath: selectionStartPath,
                            endOffset: selectionStartOffset
                        });
                    }

                    if (highlightEndComparison > 0) {
                        // Highlight ends after selection - keep the part after selection
                        newHighlights.push({
                            ...highlight,
                            id: highlight.id + '_after_' + Date.now(),
                            startPath: selectionEndPath,
                            startOffset: selectionEndOffset
                        });
                    }
                }
            });

            return newHighlights;
        });

        // Keep selection active for potential additional operations
        // selection.removeAllRanges();
        // setSelectedText('');
    };

    const handleStrikethroughSelected = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) {
            return;
        }

        const range = selection.getRangeAt(0);
        const text = selection.toString();
        if (!text.trim()) {
            return;
        }

        const contentElement = containerRef.current?.querySelector('.dx-htmleditor-content');
        if (!contentElement) {
            // Try alternative selectors
            const altContentElement = containerRef.current?.querySelector('.dx-htmleditor');
            if (altContentElement) {
                const newStrikethrough = {
                    id: 'strikethrough-' + Date.now(),
                    text: text,
                    startPath: getPathTo(range.startContainer, altContentElement),
                    startOffset: range.startOffset,
                    endPath: getPathTo(range.endContainer, altContentElement),
                    endOffset: range.endOffset,
                };
                setStrikethroughs(prev => [...prev, newStrikethrough]);
                return;
            }
            alert('Could not find the content element. Please try again.');
            return;
        }

        // Check if the selection is within our content
        if (!contentElement.contains(range.commonAncestorContainer)) {
            return;
        }

        // Get selection boundaries
        const selectionStartPath = getPathTo(range.startContainer, contentElement);
        const selectionEndPath = getPathTo(range.endContainer, contentElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        // Check if any strikethrough overlaps with the selection
        const overlappingStrikethroughs = strikethroughs.filter(strikethrough => {
            const strikethroughStartComparison = comparePaths(strikethrough.startPath, strikethrough.startOffset, selectionEndPath, selectionEndOffset);
            const strikethroughEndComparison = comparePaths(strikethrough.endPath, strikethrough.endOffset, selectionStartPath, selectionStartOffset);

            // Overlap exists if strikethrough starts before selection ends AND strikethrough ends after selection starts
            return strikethroughStartComparison < 0 && strikethroughEndComparison > 0;
        });

        if (overlappingStrikethroughs.length > 0) {
            // Remove strikethrough from selected area
            setStrikethroughs(prev => {
                const newStrikethroughs = [];

                prev.forEach(strikethrough => {
                    const isOverlapping = overlappingStrikethroughs.some(o => o.id === strikethrough.id);

                    if (!isOverlapping) {
                        // No overlap - keep as is
                        newStrikethroughs.push(strikethrough);
                    } else {
                        // Overlap detected - split and keep non-overlapping parts
                        const strikethroughStartComparison = comparePaths(strikethrough.startPath, strikethrough.startOffset, selectionStartPath, selectionStartOffset);
                        const strikethroughEndComparison = comparePaths(strikethrough.endPath, strikethrough.endOffset, selectionEndPath, selectionEndOffset);

                        if (strikethroughStartComparison < 0) {
                            // Strikethrough starts before selection - keep the part before selection
                            const beforeStrikethrough = {
                                ...strikethrough,
                                id: strikethrough.id + '_before_' + Date.now(),
                                endPath: selectionStartPath,
                                endOffset: selectionStartOffset
                            };
                            newStrikethroughs.push(beforeStrikethrough);
                        }

                        if (strikethroughEndComparison > 0) {
                            // Strikethrough ends after selection - keep the part after selection
                            const afterStrikethrough = {
                                ...strikethrough,
                                id: strikethrough.id + '_after_' + Date.now(),
                                startPath: selectionEndPath,
                                startOffset: selectionEndOffset
                            };
                            newStrikethroughs.push(afterStrikethrough);
                        }
                    }
                });

                return newStrikethroughs;
            });
        } else {
            // No overlapping strikethroughs - add new strikethrough
            const newStrikethrough = {
                id: 'strikethrough-' + Date.now(),
                text: text,
                startPath: selectionStartPath,
                startOffset: selectionStartOffset,
                endPath: selectionEndPath,
                endOffset: selectionEndOffset,
            };

            setStrikethroughs(prev => [...prev, newStrikethrough]);
        }
        // Keep selection active for potential additional operations
        // selection.removeAllRanges();
        // setSelectedText('');
    };

    const handleClearAllHighlights = () => {
        setHighlights([]);
    };

    const handleClearAllStrikethroughs = () => {
        setStrikethroughs([]);
    };

    useEffect(() => {
        const handleSelectionChange = () => {
            const selection = window.getSelection();
            const text = selection.toString().trim();

            // Only update if selection is within our container
            if (containerRef.current && text) {
                const contentElement = containerRef.current.querySelector('.dx-htmleditor-content');
                if (contentElement && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    if (contentElement.contains(range.commonAncestorContainer)) {
                        setSelectedText(text);
                        return;
                    }
                }
            }
            setSelectedText('');
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        triggerHighlight: handleHighlightSelected,
        triggerStrikethrough: handleStrikethroughSelected,
        removeHighlightFromSelection: removeHighlightFromSelection,
        clearAllHighlights: handleClearAllHighlights,
        clearAllStrikethroughs: handleClearAllStrikethroughs,
        hasSelection: () => !!selectedText,
        getSelectedText: () => selectedText,
    }));

    return (
        <div id="forceToArial" ref={containerRef} style={{ position: 'relative', ...style }} className={className}>
            <HtmlEditor
                value={htmlContent}
                className='arialFont tw-overflow-auto tw-h-[75vh]'
                readOnly
                style={{ border: 'none', boxShadow: 'none' }}
            />

            {/* Highlight layer */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                {highlightBoxes.map(box => {
                    const colorMap = {
                        yellow: 'rgba(255, 255, 0, 0.4)',
                        green: 'rgba(0, 255, 0, 0.4)',
                        pink: 'rgba(255, 182, 193, 0.4)',
                        blue: 'rgba(173, 216, 230, 0.4)'
                    };
                    return (
                        <div
                            key={box.key}
                            style={{
                                position: 'absolute',
                                top: `${box.top}px`,
                                left: `${box.left}px`,
                                width: `${box.width}px`,
                                height: `${box.height}px`,
                                backgroundColor: colorMap[box.color] || colorMap.yellow,
                                zIndex: 1,
                                pointerEvents: 'none',
                            }}
                        />
                    );
                })}
            </div>

            {/* Strikethrough layer */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                {strikethroughBoxes.map(box => (
                    <div
                        key={box.key}
                        style={{
                            position: 'absolute',
                            top: `${box.top + box.height / 2}px`,
                            left: `${box.left}px`,
                            width: `${box.width}px`,
                            height: '2px',
                            backgroundColor: 'rgba(255, 0, 0, 0.8)',
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    />
                ))}
            </div>
        </div>
    );
});

QuestionTextViewer.displayName = 'QuestionTextViewer';

export default QuestionTextViewer;

